import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ServiceCatalogFull, ServiceUpdate } from '@smile/shared';
import { ApiError, api } from '../api/client';

/**
 * Nastavení → Služby — the catalogue of extra services (mytí, čištění,
 * valet…), the legacy extraservices page. Every row is editable in place
 * and saved on its own; a row marked "smazaná" leaves the booking form
 * but stays here so old bookings keep their line. Valet (#2) can't be
 * deleted — the exclusive tariff includes it. The English/Russian/German
 * titles the web site shows are on the row too, folded under "překlady".
 */
type Draft = {
  status: 'ok' | 'deleted';
  title: string;
  titleEn: string;
  titleRu: string;
  titleDe: string;
  price: string;
  priority: string;
  public: boolean;
  canOrderCheap: boolean;
  canOrderRegular: boolean;
  canOrderExclusive: boolean;
  includedRegular: boolean;
  includedExclusive: boolean;
};

function toDraft(s: ServiceCatalogFull): Draft {
  return {
    status: s.status,
    title: s.title,
    titleEn: s.titleEn,
    titleRu: s.titleRu,
    titleDe: s.titleDe,
    price: String(s.price),
    priority: String(s.priority),
    public: s.public,
    canOrderCheap: s.canOrder.cheap,
    canOrderRegular: s.canOrder.regular,
    canOrderExclusive: s.canOrder.exclusive,
    includedRegular: s.included.regular,
    includedExclusive: s.included.exclusive,
  };
}

/** Only what changed, typed for the API; null when nothing did or a number is bad. */
function diff(s: ServiceCatalogFull, d: Draft): ServiceUpdate | null {
  const base = toDraft(s);
  const out: ServiceUpdate = {};
  if (d.status !== base.status) out.status = d.status;
  if (d.title.trim() !== base.title) out.title = d.title.trim();
  if (d.titleEn.trim() !== base.titleEn) out.titleEn = d.titleEn.trim();
  if (d.titleRu.trim() !== base.titleRu) out.titleRu = d.titleRu.trim();
  if (d.titleDe.trim() !== base.titleDe) out.titleDe = d.titleDe.trim();
  const price = Number(d.price);
  const priority = Number(d.priority);
  if (!Number.isInteger(price) || price < 0 || !Number.isInteger(priority) || priority < 0 || priority > 255) return null;
  if (price !== s.price) out.price = price;
  if (priority !== s.priority) out.priority = priority;
  for (const k of ['public', 'canOrderCheap', 'canOrderRegular', 'canOrderExclusive', 'includedRegular', 'includedExclusive'] as const) {
    if (d[k] !== base[k]) out[k] = d[k];
  }
  if (out.title === '') return null;
  return Object.keys(out).length ? out : null;
}

function errText(err: unknown): string | null {
  if (err instanceof ApiError) return err.serverMessage ?? (err.code === 'forbidden' ? 'Na tuhle akci nemáte oprávnění.' : err.message);
  return err instanceof Error ? err.message : null;
}

type BoolKey = 'public' | 'canOrderCheap' | 'canOrderRegular' | 'canOrderExclusive' | 'includedRegular' | 'includedExclusive';

function Row({ s, canWrite }: { s: ServiceCatalogFull; canWrite: boolean }) {
  const qc = useQueryClient();
  const [d, setD] = useState<Draft>(() => toDraft(s));
  const [open, setOpen] = useState(false);
  useEffect(() => setD(toDraft(s)), [s]);
  const m = useMutation({
    mutationFn: (patch: ServiceUpdate) => api.updateService(s.id, patch),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['settings', 'services'] });
      await qc.invalidateQueries({ queryKey: ['catalog'] });
    },
  });
  const patch = diff(s, d);
  const dirty = JSON.stringify(d) !== JSON.stringify(toDraft(s));
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD({ ...d, [k]: v });
  const chk = (k: BoolKey, label: string) => (
    <td className="num">
      <input type="checkbox" checked={d[k]} disabled={!canWrite} onChange={(e) => set(k, e.target.checked)} aria-label={`${s.title} — ${label}`} />
    </td>
  );
  const message = errText(m.error);
  return (
    <>
      <tr className={s.status === 'deleted' ? 'deleted' : undefined}>
        <td className="was">#{s.id}</td>
        <td>
          <input className="wide" value={d.title} maxLength={255} disabled={!canWrite} onChange={(e) => set('title', e.target.value)} aria-label={`${s.title} — název`} />
        </td>
        <td className="num">
          <input type="number" min={0} step={1} value={d.price} disabled={!canWrite} onChange={(e) => set('price', e.target.value)} aria-label={`${s.title} — cena`} />
        </td>
        <td className="num">
          <input type="number" min={0} max={255} step={1} className="short" value={d.priority} disabled={!canWrite} onChange={(e) => set('priority', e.target.value)} aria-label={`${s.title} — pořadí`} />
        </td>
        {chk('public', 'na webu')}
        {chk('canOrderCheap', 'lze objednat k levnému')}
        {chk('canOrderRegular', 'lze objednat k běžnému')}
        {chk('canOrderExclusive', 'lze objednat k exkluzivnímu')}
        {chk('includedRegular', 'v ceně běžného')}
        {chk('includedExclusive', 'v ceně exkluzivního')}
        <td className="num">
          <input
            type="checkbox"
            checked={d.status === 'deleted'}
            disabled={!canWrite || s.id === 2}
            onChange={(e) => set('status', e.target.checked ? 'deleted' : 'ok')}
            aria-label={`${s.title} — smazaná`}
            title={s.id === 2 ? 'Valet parking je součástí exkluzivního tarifu — nelze smazat.' : undefined}
          />
        </td>
        <td className="act">
          <button type="button" className="btn sm" onClick={() => setOpen(!open)} aria-expanded={open}>
            překlady
          </button>{' '}
          {canWrite && (
            <button type="button" className="btn sm primary" disabled={!dirty || !patch || m.isPending} onClick={() => patch && m.mutate(patch)}>
              {m.isPending ? '…' : 'Uložit'}
            </button>
          )}
          {m.isSuccess && !dirty && <span className="arrived-mark"> ✔</span>}
        </td>
      </tr>
      {(open || message) && (
        <tr className="translations">
          <td />
          <td colSpan={11}>
            {open && (
              <div className="efgrid three">
                <label className="ef">
                  English
                  <input value={d.titleEn} maxLength={255} disabled={!canWrite} onChange={(e) => set('titleEn', e.target.value)} />
                </label>
                <label className="ef">
                  Русский
                  <input value={d.titleRu} maxLength={255} disabled={!canWrite} onChange={(e) => set('titleRu', e.target.value)} />
                </label>
                <label className="ef">
                  Deutsch
                  <input value={d.titleDe} maxLength={255} disabled={!canWrite} onChange={(e) => set('titleDe', e.target.value)} />
                </label>
              </div>
            )}
            {message && (
              <span className="arrive-error" role="alert">
                {message}
              </span>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

export function SluzbyCard({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['settings', 'services'], queryFn: () => api.allServices() });
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const add = useMutation({
    mutationFn: () => api.createService({ title: title.trim(), price: Number(price) }),
    onSuccess: async () => {
      setTitle('');
      setPrice('');
      await qc.invalidateQueries({ queryKey: ['settings', 'services'] });
      await qc.invalidateQueries({ queryKey: ['catalog'] });
    },
  });
  const addOk = title.trim().length > 0 && Number.isInteger(Number(price)) && Number(price) >= 0 && price !== '';
  const message = errText(add.error);

  return (
    <div className="card detailcard">
      <h2>Služby</h2>
      <p className="settings-note">
        Ceník doplňkových služeb, ze kterého bere Nová rezervace i web. Každý řádek se ukládá zvlášť. Smazaná služba zmizí z
        formuláře, staré rezervace si ji nechají. „V ceně“ znamená, že ji tarif zahrnuje bez příplatku.
      </p>
      {q.isPending ? (
        <div className="empty">Načítám…</div>
      ) : q.isError ? (
        <div className="empty" role="alert">Služby se nepodařilo načíst.</div>
      ) : (
        <div className="tablewrap">
          <table className="mini settings services">
            <thead>
              <tr>
                <th>#</th>
                <th>Název</th>
                <th className="num">Cena</th>
                <th className="num">Pořadí</th>
                <th className="num">Web</th>
                <th className="num" colSpan={3}>
                  Lze objednat: levné · běžné · exkl.
                </th>
                <th className="num" colSpan={2}>
                  V ceně: běžné · exkl.
                </th>
                <th className="num">Smazaná</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(q.data as ServiceCatalogFull[]).map((s) => (
                <Row key={s.id} s={s} canWrite={canWrite} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canWrite ? (
        <form
          className="editform service-add"
          onSubmit={(e) => {
            e.preventDefault();
            if (addOk && !add.isPending) add.mutate();
          }}
        >
          <div className="efgrid">
            <label className="ef wide2">
              Nová služba
              <input value={title} maxLength={255} onChange={(e) => setTitle(e.target.value)} placeholder="název" required />
            </label>
            <label className="ef">
              Cena Kč
              <input type="number" min={0} step={1} value={price} onChange={(e) => setPrice(e.target.value)} required />
            </label>
          </div>
          <div className="efactions">
            <button type="submit" className="btn primary" disabled={!addOk || add.isPending}>
              {add.isPending ? 'Ukládám…' : 'Přidat službu'}
            </button>
            {message && (
              <span className="arrive-error" role="alert">
                {message}
              </span>
            )}
          </div>
        </form>
      ) : (
        <div className="efactions">
          <span className="empty">Služby upravuje majitel.</span>
        </div>
      )}
    </div>
  );
}
